import { EPromotionDetailSubType, EPromotionDetailType } from 'core/model/enums';
import type {
  IPromotionDetailRequest,
  IPromotionDetailResponse,
  IPromotionForAutocomplete,
  IPromotionListResponse,
  IPromotionParams,
} from 'core/model/interfaces/promotions';
import { PROMOTIONS_CONDITION_TYPE } from 'screens/promotions/promotions.constants';
import { formatDate, formatLocaleDate, formatLocaleDateTime, formatLocaleTime } from 'shared/utils/date.utils';
import { removeEmptyValues } from 'shared/utils/object.utils';
import type {
  IPromotionDetailRequestDTO,
  IPromotionDetailResponseDTO,
  IPromotionDTO,
  IPromotionListResponseDTO,
  IPromotionParamsDTO,
} from './promotions.dto';

export const toPromotionForAutocompleteList = (promotionList: Array<IPromotionDTO>): Array<IPromotionForAutocomplete> =>
  promotionList.map(({ externalId, description, type, limit, hasLimit }) => ({
    promotionId: externalId,
    promotionName: description,
    promotionType: type,
    promotionLimit: limit,
    hasLimit,
  }));

export const mapPromotionListDtoToDomain = ({
  pagination,
  promotions,
}: IPromotionListResponseDTO): IPromotionListResponse => ({
  pagination,
  promotions: promotions.map(({ externalId, startDateTime, endDateTime, type, subType, ...rest }) => ({
    id: externalId,
    startDate: startDateTime && formatLocaleDate(startDateTime),
    startTime: startDateTime && formatLocaleTime(startDateTime),
    endDate: endDateTime && formatLocaleDate(endDateTime),
    endTime: endDateTime && formatLocaleTime(endDateTime),
    type: mapPromotionDetailTypeDtoToDomain[type],
    subType: mapPromotionDetailSubTypeDtoToDomain[subType],
    ...rest,
  })),
});

export const mapPromotionDetailDtoToDomain = ({
  externalId,
  startDateTime,
  endDateTime,
  customerExternalIds,
  type,
  subType,
  conditions,
  ...rest
}: IPromotionDetailResponseDTO): IPromotionDetailResponse => ({
  id: externalId,
  startDate: startDateTime && formatDate(startDateTime, 'DD [de] MMMM YYYY'),
  endDate: endDateTime && formatDate(endDateTime, 'DD [de] MMMM YYYY'),
  startTime: startDateTime && formatDate(startDateTime, 'HH:mm A'),
  endTime: startDateTime && formatDate(endDateTime, 'HH:mm A'),
  customerIds: customerExternalIds,
  type: mapPromotionDetailTypeDtoToDomain[type],
  subType: mapPromotionDetailSubTypeDtoToDomain[subType],
  conditions: conditions.map(({ min, max, type, ...condition }, index) => {
    let range = `${min}`;
    range += max ? ` - ${max} ` : ' o más ';
    range += PROMOTIONS_CONDITION_TYPE[type];
    return { ...condition, min, max, type, range, id: index };
  }),
  ...rest,
});

export const mapPromotionDetailDomainToDto = ({ ...data }: IPromotionDetailRequest): IPromotionDetailRequestDTO =>
  removeEmptyValues({
    ...data,
  });

export const mapPromotionParamsDomainToDto = ({
  ids,
  startDate,
  endDate,
  ...rest
}: IPromotionParams): IPromotionParamsDTO =>
  removeEmptyValues({
    externalPromotionIds: ids,
    startDateTime: startDate && formatLocaleDateTime(startDate),
    endDateTime: endDate && formatLocaleDateTime(endDate),
    ...rest,
  });

export const mapPromotionDetailTypeDtoToDomain = {
  DISCOUNT: EPromotionDetailType.DISCOUNT,
  BONUS: EPromotionDetailType.BONUS,
  COMBO_DISCOUNT: EPromotionDetailType.DISCOUNT,
  COMBO_BONUS: EPromotionDetailType.BONUS,
};

export const mapPromotionDetailSubTypeDtoToDomain = {
  SIMPLE: EPromotionDetailSubType.SIMPLE,
  TIERED: EPromotionDetailSubType.TIERED,
  COMBO_DISCOUNT: EPromotionDetailSubType.COMBO,
  COMBO_BONUS: EPromotionDetailSubType.COMBO,
};
